import * as _ from "lodash-es";
import moment from "moment-timezone";
import React, { ChangeEvent, Component, createRef } from "react";
import * as d3 from "d3";
import { FormattedNumber } from "react-intl";
import IOnGetCommissionReportDTO from "../../../../models/DTO/IOnGetCommissionReportDTO";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import PeriodTypesEnum from "../../../../models/PeriodTypesEnum";
import ReportPeriodType from "../../../../models/ReportPeriodTypeEnum";
import ReportTypeEnum from "../../../../models/ReportTypeEnum";
import UserTypesEnum from "../../../../models/UserTypesEnum";
import { OwIconButton } from "../../../shared/OwIconButton/OwIconButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import CommissionReportRow from "./CommissionReportRow/CommissionReportRow";
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import IProcessed_Report from "../../../../models/IProcessed_Report";
import { Socket } from "socket.io-client";

type Props = {
    supplierModel: ISupplierModel;
    userModel: IUserUI;
    socket: Socket;
    processedReport: IProcessed_Report;
};

type State = {
    selectedUserGuid?: string;
};

class CommissionReport extends Component<Props, State> {
    readonly state: State = {
        selectedUserGuid: undefined,
    };

    private chartRef = createRef<HTMLDivElement>();
    private pieChart: any = null;

    componentDidUpdate(prevProps: Props, prevState: State) {
        // Re-render chart if the report data or selected user changes
        if (
            prevProps.processedReport !== this.props.processedReport ||
            prevState.selectedUserGuid !== this.state.selectedUserGuid
        ) {
            this.renderPieChart();
        }
    }

    // Add resize listener for responsive behavior
    handleResize = _.debounce(() => {
        this.renderPieChart();
    }, 300);

    componentDidMount() {
        this.renderPieChart();
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        // Clean up D3 elements and remove event listeners
        if (this.chartRef.current) {
            d3.select(this.chartRef.current).selectAll("*").remove();
        }
        window.removeEventListener('resize', this.handleResize);
    }

    downloadExcel = () => {
        if (this.props.processedReport) {
            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet("Commissions");

            const salesPeopleWs: { salesperson: string; ws: ExcelJS.Worksheet }[] = [];

            ws.columns = [
                { header: "Salesperson", key: "salesperson" },
                { header: "Email", key: "email" },
                { header: "Commission", key: "commission" },
            ];

            ws.getCell("A1").font = {
                bold: true,
            };

            ws.getCell("B1").font = {
                bold: true,
            };

            ws.getCell("C1").font = {
                bold: true,
            };

            const userRows = Object.keys(this.props.processedReport.report_data.userDetailsObj);

            for (const user of userRows) {
                const userDetail = this.props.processedReport.report_data.userDetailsObj[user];

                let name = userDetail?.name;
                const email = userDetail?.email;
                // tslint:disable-next-line:variable-name
                const commission_total = userDetail?.grandTotal || 0;

                if (workbook.getWorksheet(name)) {
                    name = `${userDetail?.name} (2)`;
                }

                ws.addRow({ salesperson: name, email, commission: commission_total });

                const salesPersonWs = workbook.addWorksheet(name);

                salesPersonWs.columns = [
                    { header: "Account", key: "account" },
                    { header: "Order", key: "order" },
                    { header: "Order Sub Total", key: "orderSubTotal" },
                    { header: "Amount", key: "amount" },
                ];

                salesPersonWs.getCell("A1").font = {
                    bold: true,
                };

                salesPersonWs.getCell("B1").font = {
                    bold: true,
                };

                salesPersonWs.getCell("C1").font = {
                    bold: true,
                };

                salesPersonWs.getCell("D1").font = {
                    bold: true,
                };

                const vendorGuids = Object.keys(userDetail.vendors);

                for (const vendorGuid of vendorGuids) {
                    const vendorDetail = userDetail.vendors[vendorGuid];

                    const grandTotal = vendorDetail.grandTotal;
                    const account = vendorDetail.name;

                    const orders = vendorDetail.orders;

                    for (const order of orders) {
                        const orderName = order.name;
                        const commission = order.commission;
                        const orderGuid = order.guid;
                        const orderSubTotal = order.orderSubTotal;

                        salesPersonWs.addRow({ account, order: orderName, orderSubTotal, amount: commission });
                    }
                }

                salesPeopleWs.push({ salesperson: name, ws: salesPersonWs });
            }

            const periodStart = moment(this.props.processedReport.period_start).format("YYYY-MM-DD");
            const periodEnd = moment(this.props.processedReport.period_end).format("YYYY-MM-DD");

            workbook.xlsx.writeBuffer().then((data) => {
                const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `${periodStart}-${periodEnd}-orderwerks-commission-report.xlsx`;
                anchor.click();
                window.URL.revokeObjectURL(url);
            });
        }
    };

    downloadCSV = () => {
        if (this.props.processedReport.report_data) {
            const headerRow = ["Salesperson", "Email", "Commission"];

            const dataRows: any[][] = [];

            const userRows = Object.keys(this.props.processedReport.report_data?.userDetailsObj);

            for (const user of userRows) {
                const userDetail = this.props.processedReport.report_data?.userDetailsObj[user];

                const name = userDetail?.name;
                const email = userDetail?.email;
                const commission_total = userDetail?.grandTotal;

                const userRow = [name, email, commission_total];

                dataRows.push(userRow);
            }

            const periodStart = moment(this.props.processedReport.period_start).format("YYYY-MM-DD");
            const periodEnd = moment(this.props.processedReport.period_end).format("YYYY-MM-DD");

            const rows = [headerRow, ...dataRows, [`${periodStart} - ${periodEnd}`, "", this.props.processedReport.report_data.grandTotal]];

            const csvContent = "data:text/csv;charset=utf-8," + rows.map((e) => e.join(",")).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `${periodStart}-${periodEnd}-orderwerks-commission-report.csv`);

            link.click();
        }
    };

    renderPieChart = () => {
        if (!this.props.processedReport.report_data || !this.chartRef.current) {
            return;
        }

        // Clear any existing chart
        d3.select(this.chartRef.current).selectAll("*").remove();

        const userGuids = Object.keys(this.props.processedReport.report_data.userDetailsObj);

        const users = _.map(userGuids, (guid: string) => {
            const userDetailsObj = this.props.processedReport.report_data!.userDetailsObj;
            const userDetail = userDetailsObj[guid];
            return {
                name: userDetail.name,
                value: Number(userDetail.grandTotal)
            };
        });

        // Chart dimensions - make responsive based on container width
        const containerWidth = this.chartRef.current.clientWidth;
        const containerHeight = this.chartRef.current.clientHeight;
        const width = Math.min(containerWidth, 600); // Cap at 600px for larger screens
        const height = 450;
        const margin = 50; // Increased margin for labels

        // The radius of the pie chart is half the smallest side
        const radius = Math.min(width, height) / 2 - margin;

        // Create the SVG container
        const svg = d3.select(this.chartRef.current)
            .append("svg")
            .attr("width", width)
            .attr("height", height)
            .append("g")
            .attr("transform", `translate(${width / 2},${height / 2})`);

        // Add title
        svg.append("text")
            .attr("text-anchor", "middle")
            .attr("y", height / 2 - 10)
            .attr("font-size", "16px")
            .attr("font-weight", "bold")
            .text("Commission By User");

        // Set up color scale
        const color = d3.scaleOrdinal(d3.schemeCategory10);

        // Create pie layout
        const pie = d3.pie<any>()
            .value(d => d.value)
            .sort(null);

        // Generate the arcs
        const arc = d3.arc()
            .innerRadius(0)
            .outerRadius(radius);

        // For labels - use a more dynamic approach to prevent overlap
        const labelArc = d3.arc()
            .innerRadius(radius * 0.7)
            .outerRadius(radius * 1.0);

        // Add tooltip
        const tooltip = d3.select(this.chartRef.current)
            .append("div")
            .attr("class", "d3-tooltip")
            .style("position", "absolute")
            .style("background-color", "white")
            .style("border", "1px solid #ddd")
            .style("border-radius", "4px")
            .style("padding", "10px")
            .style("pointer-events", "none")
            .style("opacity", 0)
            .style("z-index", 100);

        // Generate the pie chart
        const arcs = svg.selectAll(".arc")
            .data(pie(users))
            .enter()
            .append("g")
            .attr("class", "arc");

        // Draw the arcs
        arcs.append("path")
            .attr("d", arc as any)
            .attr("fill", (d, i) => color(i.toString()))
            .attr("stroke", "#fff")
            .style("stroke-width", "2px")
            .on("mouseover", function(event, d) {
                tooltip.transition()
                    .duration(200)
                    .style("opacity", 0.9);
                tooltip.html(`${d.data.name}: $${d.data.value.toFixed(2)}`)
                    .style("left", (event.pageX + 10) + "px")
                    .style("top", (event.pageY - 28) + "px");
            })
            .on("mouseout", function() {
                tooltip.transition()
                    .duration(500)
                    .style("opacity", 0);
            });

        // Improved label positioning to avoid overlap
        const totalValue = _.sumBy(users, 'value');
        const angleOffset = 0.1; // Small offset to adjust positions

        // Create text label elements
        const textLabels = arcs.append("text")
            .attr("class", "pie-label")
            .style("font-size", "12px")
            .style("font-weight", "500")
            .style("fill", "#333")
            .style("pointer-events", "none");

        // Add label lines
        arcs.append("polyline")
            .attr("class", "label-line")
            .style("fill", "none")
            .style("stroke", "#999")
            .style("stroke-width", "1px")
            .style("opacity", d => d.data.value > (totalValue * 0.03) ? 1 : 0)
            .attr("points", function(d) {
                const pos = labelArc.centroid(d as any);
                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                const x = Math.sin(midAngle) * (radius + 20);
                const y = -Math.cos(midAngle) * (radius + 20);

                // Convert array of points to a string format
                const pointsArray = [arc.centroid(d as any), labelArc.centroid(d as any), [x, y]];
                return pointsArray.map(point => point[0] + ',' + point[1]).join(' ');
            });

        // Add the actual text labels at the end of the lines
        textLabels
            .each(function(d) {
                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                const x = Math.sin(midAngle) * (radius + 30);
                const y = -Math.cos(midAngle) * (radius + 30);

                // Only show label if the slice is big enough
                if (d.data.value > (totalValue * 0.03)) {
                    d3.select(this)
                        .attr("transform", `translate(${x}, ${y})`)
                        .attr("text-anchor", x > 0 ? "start" : "end")
                        .text(d.data.name);
                }
            });
    };

    getCommissionByUserPieChart = () => {
        if (!this.props.processedReport.report_data) {
            return null;
        }

        return (
            <div className={"mt-2 d-flex justify-content-center"}>
                <div
                    ref={this.chartRef}
                    style={{ width: "100%", maxWidth: "700px", height: "450px" }}
                ></div>
            </div>
        );
    };

    handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const state: any = {
            [name]: value,
        };

        this.setState(state);
    };

    savePdf = () => {};

    getReportContent = (usersArray: any, userGuids: string[]) => {
        if (!this.props.processedReport.report_data) {
            return null;
        }

        const userDetailsObj: any = this.props.processedReport.report_data ? this.props.processedReport.report_data.userDetailsObj : {};

        let grandTotal: number | undefined;

        if (this.state.selectedUserGuid) {
            usersArray.forEach((detail: any) => {
                if (detail.guid === this.state.selectedUserGuid) {
                    grandTotal = detail.grandTotal;
                }
            });
        }

        return (
            <div>
                <div>
                    <div className={"d-flex"}>
                        <OwLabel title={"total_commissions"} className={"me-auto"} />
                        <span>
                            <FormattedNumber
                                value={Number(grandTotal || this.props.processedReport.report_data.grandTotal)}
                                minimumFractionDigits={2}
                            />
                        </span>
                    </div>
                    <div>
                        <OwTitle title={"breakdown"} tag={"h5"} />
                        {_.map(userGuids, (userGuid: string, index: number) => {
                            if (this.state.selectedUserGuid) {
                                if (userGuid !== this.state.selectedUserGuid) {
                                    return null;
                                }
                            }

                            return <CommissionReportRow key={index} userDetailRow={userDetailsObj[userGuid]} />;
                        })}
                    </div>
                    <div></div>
                </div>
                <div>{this.getCommissionByUserPieChart()}</div>
            </div>
        );
    };

    render() {
        const userGuids = this.props.processedReport.report_data ? Object.keys(this.props.processedReport.report_data.userDetailsObj) : [];
        const usersArray: any[] = userGuids.map((guid) => {
            return {
                guid,
                // @ts-expect-error
                name: this.props.processedReport.report_data?.userDetailsObj[guid].name,
                // @ts-expect-error
                grandTotal: this.props.processedReport.report_data?.userDetailsObj[guid].grandTotal,
            };
        });

        return (
            <div className="CommissionReport">
                <div className={"d-flex"}>
                    <OwTitle title={"commission_report"} leftIconName={"fad fa-money-check-edit-alt"} tag={"h2"} />
                    <OwTitle title={": " + this.props.processedReport.title} disableTranslation={true} tag={"h2"} />
                </div>

                {this.props.processedReport.report_data ? (
                    <div>
                        <div>
                            <OwLabel className={"me-3"} title={"excel"} leftIconClass={"fas fa-download"} fn={this.downloadExcel} />
                            <OwLabel title={"csv"} leftIconClass={"fas fa-download"} fn={this.downloadCSV} />
                        </div>
                        {this.props.userModel.isSupplierUser &&
                        this.props.userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN ? (
                            <div className={"mb-2"}>
                                <OwLabel title={"salesperson"} noBottomMargin={true} />
                                <div className={".g-0 col-12 col-md-4 col-lg-3"}>
                                    <OwSelectOption
                                        titleProp={"name"}
                                        valueProp={"guid"}
                                        options={usersArray}
                                        name={"selectedUserGuid"}
                                        fn={this.handleChange}
                                        showBlankFirst={true}
                                    />
                                </div>
                            </div>
                        ) : null}
                        {this.getReportContent(usersArray, userGuids)}
                    </div>
                ) : null}
            </div>
        );
    }
}

export default CommissionReport;
